import classNames from 'classnames';
import { FieldProps, useFormikContext } from 'formik';
import _ from 'lodash';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { Location, LocationOption } from 'modules/locations/domain/interfaces/location';
import locationsRepository from 'modules/locations/domain/repositories/locations.repository';
import { time } from 'shared/constants/time';
import React, { useCallback } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { UDFormComponentProps } from 'ud-form/components/types';
import UDSelect, { UDSelectProps } from 'ud-form/components/select/component';
import UDFormFieldWrapper from 'ud-form/wrapper';

type LocationSelectProps<isMulti extends boolean> = UDFormComponentProps &
  Omit<UDSelectProps<isMulti>, 'options'> & {
    selectComponent?: (props: any) => React.ReactElement<any, any> | null;
    formatOptionLabel?: (location: Location) => string;
    filterLocations?: (locations: Location[]) => Location[];
    availableLocations?: Location[];
    options?: LocationOption[];
  };

type Returning = <isMulti extends boolean = false>(props: LocationSelectProps<isMulti>) => JSX.Element;

const LocationSelect: Returning = (props) => {
  const {
    name,
    containerProps,
    required,
    label,
    labelProps,
    selectComponent,
    filterLocations,
    availableLocations,
    options,
    ...otherSelectProps
  } = props;

  const { setFieldValue } = useFormikContext();

  const tournamentId = useSelector(authStoreUserTournamentId);
  const { data: locations, isFetching: isLocationsFetching } = useQuery({
    queryKey: ['locations', tournamentId],
    queryFn: () => {
      if (!tournamentId) return Promise.resolve([]);
      return locationsRepository.fetchLocationsOptions({ tournamentId });
    },
    enabled: !!tournamentId,
    keepPreviousData: true,
    staleTime: 60 * time.minute,
  });

  const onChange = useCallback(
    (value: any, actionMeta: any) => {
      setFieldValue(name, value);

      if (props.onChange) {
        props.onChange(value, actionMeta);
      }
    },
    [name, props, setFieldValue],
  );

  const Component = selectComponent || UDSelect;

  return (
    <UDFormFieldWrapper name={name} containerProps={containerProps} label={label} labelProps={labelProps} required={required}>
      {({ field }: FieldProps<any>) => (
        <Component
          {...otherSelectProps}
          {...field}
          divProps={{
            className: classNames('w-full', otherSelectProps?.className),
          }}
          options={_.unionBy(locations ?? [], options ?? [], 'value')}
          onChange={onChange}
          isLoading={isLocationsFetching}
        />
      )}
    </UDFormFieldWrapper>
  );
};

export default LocationSelect;
